import React, { useState } from "react";
import Flex, { VerticalFlex } from "@vahak/core-ui/dist/layout/Flex";
import ModalOrBottomSheetWrapper, {
    ModalOrBottomSheetWrapperProps
} from "@vahak/core-ui/dist/components/ModalOrBottomSheetWrapper";
import Button from "@vahak/core-ui/dist/components/Button";
import Typography from "@vahak/core-ui/dist/components/Typography";
import { toast } from "@vahak/core/dist/components/toast/toast";
import Select, { SelectOption } from "@vahak/core-ui/dist/components/Select";
import DotLoader from "@vahak/core-ui/dist/components/DotLoader";
import TextLabelAndValue from "../ticket-system/common/TextLabelAndValue/TextLabelAndValue";
// Methods
import { stringOperations } from "@vahak/core-ui/dist/methods/string";
import { generateSelectOption } from "../../../methods/uiUtil";
import { formatPhoneNumber } from "../../../methods/formatPhoneNumber";
import { CSUserData } from "../../../services/admin-user/useGetCSUsers";
import { useGetTicketsQuery } from "../../../hooks/useGetTicketsQuery";
import { useLoadAssignv2 } from "../../../services/ticket-system/useLoadAssignv2";
import { useAssignTicket } from "../../../services/ticket-system/useAssignTicket";
// Type
import { IAssignedAgentDetail } from "../../../services/load/useGetLoadMarketV2/type";
import { ApiErrorResponseType } from "@vahak/core/dist/custom-types/api-error-response-type";
import SearchCsUserOption from "../dashboard-user/SearchCsUserOption";
import { formatDate, getRelativeTimeString } from "@vahak/core-ui/dist/methods/time";
import { DotSymbol } from "@vahak/core-ui/dist/constants/symbol";
import { getJoinedStringFromArray } from "@vahak/core-ui/dist/methods";
import { LoadTicketType, TicketStatusMap, TicketType } from "../ticket-system-helper/constants";
import { TicketData } from "../ticket-system-helper/types";
import { UserType } from "@vahak/core/dist/constants";
import { useCsDashboard } from "@vahak/core/dist/hooks";

export interface AssignedCsPayload {
    loadId?: number;
    ticketType?: LoadTicketType | TicketType;
    ticket?: TicketData;
}
interface AssignedCsPopupProps extends Pick<ModalOrBottomSheetWrapperProps, "id" | "isOpen" | "onToggle"> {
    data?: IAssignedAgentDetail;
    payload?: AssignedCsPayload;
    onSuccess: () => void;
}

export const generateCsUserOption = async (str: string, callback: Function, csSearchMethod: Function) => {
    let phoneNumber: string | undefined = stringOperations(str, ["removeNonNumeric", "trim", "truncate:10"]);

    phoneNumber = phoneNumber.length === 10 ? phoneNumber : undefined;
    if (phoneNumber && csSearchMethod) {
        const data = await csSearchMethod({ phoneNumber });
        callback(getCsUserListOption(data?.data?.details ?? []));
    } else {
        callback([]);
    }
};

const getCsUserListOption = (list: CSUserData[]) => {
    return list.map((user) =>
        generateSelectOption([user.name, `(${user.phone_number})`].join(" "), user, user.user_id)
    );
};

export const getLoadAssignmentTimeStr = (
    data?: IAssignedAgentDetail,
    type: "long" | "short" = "long",
    requireAssignee = true
) => {
    return (
        (!requireAssignee || !!data?.assigned_to) &&
        !!data?.assigned_at &&
        getJoinedStringFromArray(
            [
                formatDate(data.assigned_at, type === "long" ? "hh:mm a -  DD/MM" : "h:mm a -  DD/MM"),
                getRelativeTimeString(new Date(data.assigned_at))
            ],
            ` ${DotSymbol} `
        )
    );
};

const AssignmentDropdownOption = ["Generated Lead", "Language Barrier", "Unavailability"].map((v) =>
    generateSelectOption(v, v, v)
);

export const ticketTypeToAgentRoleMap: Partial<Record<number, UserType>> = {
    [LoadTicketType.VERIFICATION]: UserType.VERIFICATION_EXECUTIVE,
    [LoadTicketType.FULFILLMENT]: UserType.FULLFILMENT_EXECUTIVE,
    [LoadTicketType.PP_PLACEMENT]: UserType.PP_PLACEMENT,
    [LoadTicketType.PP_LOADING]: UserType.PP_LOADING,
    [LoadTicketType.PP_UNLOADING]: UserType.PP_UNLOADING,
    [TicketType.IN_TRANSIT_CALLBACK]: UserType.IN_TRANSIT_SUPPORT
};

const AssignedCsPopup = ({ data, payload, id, isOpen, onToggle, onSuccess }: AssignedCsPopupProps) => {
    const { isDeveloper } = useCsDashboard();
    const { mutateAsync: reassignLoadV2 } = useLoadAssignv2();
    const { mutateAsync: assignTicket } = useAssignTicket();
    const [selectedUser, setSelectedUser] = useState<CSUserData>();
    const [remarks, setRemarks] = useState<SelectOption<string>>();
    const [fetchedTicket, setFetchedTicket] = useState<TicketData>();
    const passedTicket = payload?.ticket;
    const shouldFetchTicket = !passedTicket && !!payload?.loadId && !!payload?.ticketType;
    const tktData = passedTicket ?? fetchedTicket;

    const onContinue = async () => {
        try {
            if (!selectedUser?.company_id || !tktData?.id) {
                return;
            }

            if (passedTicket) {
                await assignTicket({
                    csCompanyId: selectedUser.company_id,
                    ticketId: tktData.id,
                    override: !!data?.assigned_to
                });
            } else if (payload?.loadId && payload.ticketType) {
                await reassignLoadV2({
                    loadId: payload.loadId,
                    agentCompanyId: selectedUser.company_id,
                    ticketType: payload.ticketType,
                    remarks: remarks?.value
                });
            } else {
                return;
            }

            onSuccess?.();
            onToggle?.(false);
        } catch (error) {
            toast.error((error as ApiErrorResponseType).message);
        }
    };

    const ticketQueryResponse = useGetTicketsQuery(
        {
            ticket_type: payload?.ticketType,
            referenceId: [payload?.loadId!],
            limit: 1
        },
        {
            onSuccess: (data) => {
                const result = data?.at(0);
                setFetchedTicket(result);
                if (!result?.id) {
                    toast.dismiss();
                    toast.error("Re-assignment not possible");
                    onToggle?.(false);
                }
            },
            onError: (err: any) => {
                toast.error((err as ApiErrorResponseType).message);
            },
            enabled: shouldFetchTicket,
            retry: false
        }
    );

    const assignedName = data?.name || tktData?.assigned_to_details?.company_name;
    const assignedPhone = isDeveloper ? data?.phone_number : undefined;
    const formattedPhone = assignedPhone ? `+91-${formatPhoneNumber(assignedPhone.replace("+91", ""))}` : "";
    const dataToShow = {
        Type: LoadTicketType[payload?.ticketType!] || "",
        "Currently Assigned To": assignedName
            ? [assignedName, formattedPhone ? `(${formattedPhone})` : ""].filter(Boolean).join(" ")
            : "None",
        "Update By": tktData?.updated_by_details?.company_name,
        "Assignemnt Type": tktData?.meta_data?.assignment_source,
        Remarks: tktData?.meta_data?.assignment_reason || "N/A"
    };

    const roleType = ticketTypeToAgentRoleMap[payload?.ticketType!];

    const canContinue =
        !!selectedUser?.user_id &&
        !!remarks?.value.trim() &&
        !!tktData?.id &&
        [TicketStatusMap.Unverified, TicketStatusMap.Resolving].includes(tktData?.status);

    return (
        <ModalOrBottomSheetWrapper
            titleText="Assigned To"
            {...{ id, isOpen: isOpen && (!!passedTicket || ticketQueryResponse?.isFetched), onToggle }}
            sheetBodyMinHeight={"20dvh"}
        >
            <Flex flexDirection="column" gap={32}>
                {!!data?.assigned_to && !!data?.assigned_at && (
                    <Typography>
                        <Typography>Assigned At : </Typography>
                        <Typography>{getLoadAssignmentTimeStr(data)}</Typography>
                    </Typography>
                )}

                <Typography size="md">
                    {Object.entries(dataToShow).map(([key, val]) => {
                        return (
                            <TextLabelAndValue
                                label={key}
                                value={shouldFetchTicket && ticketQueryResponse?.isFetching ? <DotLoader /> : val}
                            />
                        );
                    })}
                </Typography>

                <VerticalFlex style={{ width: 400 }} gap={20}>
                    <SearchCsUserOption
                        filter={{
                            limit: 10,
                            roleType
                        }}
                        onSelect={(assignedToCsUserData) => {
                            setSelectedUser(assignedToCsUserData);
                        }}
                        selectedUser={selectedUser}
                        searchProps={{
                            placeholder: "Search using Phone (8295 XXX XXX)",
                            label: "Select new assignee"
                        }}
                    />
                    <Select
                        option={AssignmentDropdownOption}
                        value={remarks}
                        onChange={(v) => setRemarks(v as any)}
                        isSearchable
                        headingLabel="Remarks"
                        placeholder=""
                    />
                </VerticalFlex>

                <Flex justifyContent="flex-end" style={{ marginTop: 20 }}>
                    <Button onClick={onContinue} disabled={!canContinue}>
                        Update
                    </Button>
                </Flex>
            </Flex>
        </ModalOrBottomSheetWrapper>
    );
};

export default AssignedCsPopup;
